import { useState, useRef, useEffect } from "react";
import { Input, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { MarkdownKatex } from "./import/MarkdownKatex";

const { TextArea } = Input;
const { Text } = Typography;

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** shown above the field in edit mode */
  label?: string;
  /** if true, shows "(optional)" hint and allows empty */
  optional?: boolean;
  /** if true, highlights field as required and empty */
  error?: boolean;
}

export function CardFieldEditor({
  value,
  onChange,
  placeholder = "Click to edit…",
  maxLength = 500,
  rows = 4,
  label,
  optional = false,
  error = false,
}: Props) {
  const [editing, setEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Enter edit mode
  const startEdit = () => setEditing(true);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (editing) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [editing]);

  // Click outside → exit edit mode
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setEditing(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editing]);

  // Escape → exit edit mode
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setEditing(false);
  };

  const isEmpty = !value.trim();

  return (
    <div ref={containerRef}>
      {label && (
        <Text
          type="secondary"
          style={{ fontSize: 11, display: "block", marginBottom: 4 }}
        >
          {label}
          {optional && " (optional)"}
        </Text>
      )}

      {!editing ? (
        /* ── VIEW MODE ── */
        <div
          onClick={startEdit}
          style={{
            minHeight: rows * 22,
            maxHeight: rows * 22 * 3,
            overflowY: "auto",
            padding: "6px 28px 6px 10px",
            border: `1px solid ${error && isEmpty ? "#ff4d4f" : "#d9d9d9"}`,
            borderRadius: 6,
            cursor: "text",
            background: isEmpty ? (error ? "#fff2f0" : "#fafafa") : "#fff",
            position: "relative",
          }}
        >
          {isEmpty ? (
            <Text
              type="secondary"
              style={{ fontSize: 13, fontStyle: "italic" }}
            >
              {optional ? `${placeholder} (optional)` : placeholder}
            </Text>
          ) : (
            <MarkdownKatex content={value} />
          )}
          <EditOutlined
            style={{
              position: "absolute",
              top: 7,
              right: 8,
              color: "#ccc",
              fontSize: 11,
            }}
          />
        </div>
      ) : (
        /* ── EDIT MODE: stacked ── */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            border: "1px solid #4096ff",
            borderRadius: 6,
            padding: 8,
            background: "#f8fbff",
          }}
        >
          <TextArea
            ref={textareaRef as React.Ref<any>}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            showCount
            rows={rows}
            style={{ fontFamily: "monospace", fontSize: 13 }}
          />
          {value.trim() && (
            <div
              style={{
                padding: "6px 8px",
                border: "1px solid #e2e8f0",
                borderRadius: 4,
                background: "#fff",
                maxHeight: rows * 22 * 2,
                overflowY: "auto",
              }}
            >
              <MarkdownKatex content={value} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
